/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { floorPlanIconSvg } from '@kaiizen/kai-floor-plan';
import { useLengthFormatter } from '@kaiizen/kai-floor-plan-editor';
import { flooringAreaValueNamePresenter } from '@kaiizen/kai-presenters';
import { getGroupFloorPlanRoom, getGroupMeasurements, groupHasRoomAreaTemplate, type ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';

import { getGroupDimensions, getGroupFlooringAreas } from '@/Client/Lib/roomGroupMeasurementsLib';
import { groupNamePresenter } from '@/Client/Presenters';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { type FlooringHatchPattern, FlooringHatchSwatch } from './FlooringHatchSwatch';

/** Designer's hover/selected lemon wash (Kai-Merged mock) — between the `wash` and `accent` tokens. */
const HOVER_WASH = '#FBFCC9';

/** Diagonal paper hatch marking unmeasured rooms; the panel's grid shows through the gaps. */
const UNMEASURED_HATCH = `repeating-linear-gradient(45deg, ${COLORS.paper} 0, ${COLORS.paper} 4px, transparent 4px, transparent 9px)`;

const CARD_GRID_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_GRID_STYLE;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(268px, 1fr));
  gap: 14px;
`;

const CARD_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_STYLE;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 16px 16px 14px;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
  color: ${COLORS.ink};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;

  &:hover {
    border-color: ${COLORS.ink};
    background: ${HOVER_WASH};
  }
`;

/** Unmeasured rooms keep the solid border but trade the white fill for the hatch, hover included. */
const CARD_UNMEASURED_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_UNMEASURED_STYLE;
  background: ${UNMEASURED_HATCH};

  &:hover {
    background: ${UNMEASURED_HATCH};
  }
`;

/** Dimensions sit beside the name so they and the area figure never compete on one line. */
const CARD_TOP_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_TOP_STYLE;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const NAME_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_NAME_STYLE;
  font-family: ${FONT.display};
  font-size: ${pxToRem(16)};
  font-weight: 700;
  line-height: 1.25;
  color: ${COLORS.ink};
`;

const NAME_UNMEASURED_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_NAME_UNMEASURED_STYLE;
  color: ${COLORS.midtone};
`;

const DIMENSIONS_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_DIMENSIONS_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12)};
  font-weight: 500;
  color: ${COLORS.midtone};
  white-space: nowrap;
  text-align: right;
`;

const NOT_MEASURED_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_NOT_MEASURED_STYLE;
  font-style: italic;
`;

const PLAN_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_PLAN_STYLE;
  display: block;
  flex-shrink: 0;
  width: 84px;
  height: 58px;
  margin: 0 auto;
  color: ${COLORS.ink};

  & > svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const FOOTER_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_FOOTER_STYLE;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding-top: 2px;
`;

const FOOTER_LABEL_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_FOOTER_LABEL_STYLE;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
  white-space: nowrap;
`;

const AREA_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_AREA_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(13.5)};
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${COLORS.ink};
  white-space: nowrap;
  text-align: right;
`;

const AREA_UNMEASURED_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_AREA_UNMEASURED_STYLE;
  color: ${COLORS.stroke};
`;

const UNIT_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_UNIT_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(10)};
  font-weight: 500;
  letter-spacing: 0;
  color: ${COLORS.midtone};
  margin-left: 2px;
`;

const MATERIALS_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_MATERIALS_STYLE;
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${COLORS.stroke};
  padding-top: 4px;
`;

const MATERIAL_ROW_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_MATERIAL_ROW_STYLE;
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  gap: 9px;
  padding: 6px 0;

  & + & {
    border-top: 1px solid ${COLORS.stroke};
  }
`;

const MATERIAL_LABEL_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_MATERIAL_LABEL_STYLE;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MATERIAL_VALUE_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_MATERIAL_VALUE_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12.5)};
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${COLORS.ink};
  white-space: nowrap;
`;

const MATERIAL_UNIT_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_MATERIAL_UNIT_STYLE;
  font-size: ${pxToRem(9.5)};
  margin-left: 2px;
`;

/** The total's label runs under the swatches, so its value still lines up with the rows above. */
const MATERIAL_TOTAL_ROW_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_MATERIAL_TOTAL_ROW_STYLE;
  grid-template-columns: 1fr auto;
`;

const MATERIAL_TOTAL_LABEL_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_MATERIAL_TOTAL_LABEL_STYLE;
  color: ${COLORS.ink};
`;

const NO_FLOORING_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARD_NO_FLOORING_STYLE;
  grid-column: 1 / -1;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10.5)};
  font-weight: 500;
  font-style: italic;
  letter-spacing: 0.02em;
  text-transform: none;
  color: ${COLORS.midtone};
`;

const EMPTY_STATE_STYLE = css`
  label: FLOOR_PLAN_GROUP_CARDS_EMPTY_STATE_STYLE;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.midtoneFaded};
`;

const UnmeasuredPlanPlaceholder = () => (
  <svg css={PLAN_STYLE} viewBox='0 0 84 58' fill='none' aria-hidden='true'>
    <rect x='4' y='8' width='76' height='42' fill='none' stroke={COLORS.stroke} strokeWidth='1.25' strokeDasharray='3 3' />
  </svg>
);

const SquareFeet = ({ area }: { area: number }) => (
  <>
    {Math.round(area).toLocaleString()}
    <span css={UNIT_STYLE}>sqft</span>
  </>
);

/**
 * The room's footprint as `width × length × height` in the reader's preferred
 * units. Height is omitted rather than guessed when the group predates it.
 */
const formatRoomDimensions = (
  { height, length, width }: { height?: number; length?: number; width?: number },
  formatLength: (lengthFeet: number) => string,
) =>
  [width, length, height]
    .flatMap(dimension => dimension == null ? [] : [formatLength(dimension)])
    .join(' × ');

const MaterialRow = ({
  area,
  label,
  pattern,
}: {
  area: number;
  label: string;
  pattern: FlooringHatchPattern;
}) => (
  <div css={MATERIAL_ROW_STYLE}>
    <FlooringHatchSwatch pattern={pattern} />
    <span css={MATERIAL_LABEL_STYLE}>{label}</span>
    <span css={MATERIAL_VALUE_STYLE}>
      {Math.round(area).toLocaleString()}
      <span css={[UNIT_STYLE, MATERIAL_UNIT_STYLE]}>sqft</span>
    </span>
  </div>
);

const FloorPlanGroupCard = ({
  flooringHatchPatterns,
  flooringValueNames,
  group,
  onSelectGroup,
}: {
  flooringHatchPatterns: Record<string, FlooringHatchPattern>;
  flooringValueNames: readonly string[];
  group: ScanGroupOptimized;
  onSelectGroup?: (groupId: string) => void;
}) => {
  const roomFloorPlan = getGroupFloorPlanRoom(group);
  const { area } = getGroupMeasurements(group);
  const formatLength = useLengthFormatter();
  const dimensions = formatRoomDimensions(getGroupDimensions(group), formatLength);
  const materials = getGroupFlooringAreas(group, flooringValueNames);
  const flooringTotal = materials.reduce((sum, material) => sum + material.area, 0);
  return (
    <button
      type='button'
      css={[CARD_STYLE, !roomFloorPlan && CARD_UNMEASURED_STYLE]}
      data-testid='floor-plan-group-tile'
      data-measured={roomFloorPlan ? 'true' : 'false'}
      onClick={onSelectGroup ? () => onSelectGroup(group.id) : undefined}
    >
      <span css={CARD_TOP_STYLE}>
        <span css={[NAME_STYLE, !roomFloorPlan && NAME_UNMEASURED_STYLE]}>{groupNamePresenter(group)}</span>
        <span css={[DIMENSIONS_STYLE, !roomFloorPlan && NOT_MEASURED_STYLE]}>
          {roomFloorPlan && dimensions ? dimensions : 'Not measured'}
        </span>
      </span>
      {roomFloorPlan
        ? (
          <span
            css={PLAN_STYLE}
            aria-hidden='true'
            dangerouslySetInnerHTML={{
              __html: floorPlanIconSvg(roomFloorPlan, {
                lineThickness: 2,
                padding: 3,
                lineColor: 'currentColor',
              }),
            }}
          />
        )
        : <UnmeasuredPlanPlaceholder />}
      <span css={FOOTER_STYLE}>
        <span css={FOOTER_LABEL_STYLE}>Total sqft</span>
        <span css={[AREA_STYLE, area == null && AREA_UNMEASURED_STYLE]}>
          {area != null ? <SquareFeet area={area} /> : '—'}
        </span>
      </span>
      <span css={MATERIALS_STYLE}>
        {materials.length
          ? materials.map(({ area: materialArea, valueName }) => (
            <MaterialRow
              key={valueName}
              area={materialArea}
              label={flooringAreaValueNamePresenter(valueName)}
              pattern={flooringHatchPatterns[valueName]}
            />
          ))
          : (
            <div css={MATERIAL_ROW_STYLE}>
              <span css={NO_FLOORING_STYLE}>No flooring specified</span>
            </div>
          )}
        {/* One material already has its total on its own row. */}
        {materials.length > 1 && (
          <div css={[MATERIAL_ROW_STYLE, MATERIAL_TOTAL_ROW_STYLE]}>
            <span css={[MATERIAL_LABEL_STYLE, MATERIAL_TOTAL_LABEL_STYLE]}>Flooring total</span>
            <span css={MATERIAL_VALUE_STYLE}>
              {Math.round(flooringTotal).toLocaleString()}
              <span css={[UNIT_STYLE, MATERIAL_UNIT_STYLE]}>sqft</span>
            </span>
          </div>
        )}
      </span>
    </button>
  );
};

/**
 * Overall-scope floor plan overview ("room index" in the Kai-Merged design):
 * one card per room group (any group whose template exposes room
 * measurements), in sidebar order. Measured groups show their floor plan
 * outline, footprint dimensions, area and per-material flooring rollup;
 * unmeasured groups render as hatched "Not measured" ghosts inviting
 * measurement.
 *
 * The plan outline is deliberately left unhatched: we know a kitchen is 40%
 * tile, not *where* the tile is, and painting bands inside the shape would
 * claim an arrangement we do not have. The rollup carries the material story.
 */
export const FloorPlanGroupTiles = ({
  flooringHatchPatterns,
  flooringValueNames,
  groups,
  onSelectGroup,
}: {
  flooringHatchPatterns: Record<string, FlooringHatchPattern>;
  flooringValueNames: readonly string[];
  groups: readonly ScanGroupOptimized[];
  onSelectGroup?: (groupId: string) => void;
}) => {
  const roomGroups = groups.filter(groupHasRoomAreaTemplate);
  if (!roomGroups.length) return <div css={EMPTY_STATE_STYLE}>No room groups</div>;
  return (
    <div css={CARD_GRID_STYLE} data-testid='floor-plan-group-tiles'>
      {roomGroups.map(group => (
        <FloorPlanGroupCard
          key={group.id}
          flooringHatchPatterns={flooringHatchPatterns}
          flooringValueNames={flooringValueNames}
          group={group}
          onSelectGroup={onSelectGroup}
        />
      ))}
    </div>
  );
};
